import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, Minus, Trash2, ShoppingBag, Truck, Phone } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useModals } from "@/context/ModalContext";
import { GOOGLE_MEET_LINK, PHONE_HREF } from "@/lib/config";
import { Link } from "react-router-dom";

export const CartDrawer = () => {
  const { cart, suggestions, wgReminder, open, setOpen, updateItem, removeItem, addItem } = useCart();
  const { openWhiteGlove, openQuote } = useModals();

  const requestQuote = () => {
    const purpose = cart.lines.map((l) => `${l.title}${l.quantity > 1 ? ` x${l.quantity}` : ""}`).join(", ");
    setOpen(false);
    setTimeout(() => openQuote(purpose), 250);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer"
        className="bg-softwhite text-espresso w-full sm:max-w-md flex flex-col p-0 border-l-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/10">
          <SheetTitle className="font-serif text-2xl text-espresso flex items-center gap-2">
            <ShoppingBag size={20} /> Your Cart ({cart.itemCount})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.lines.length === 0 ? (
            <div className="text-center py-16 text-espresso/60" data-testid="cart-empty">
              <ShoppingBag size={40} className="mx-auto mb-4 opacity-40" />
              <p>Your cart is empty.</p>
              <Link to="/shop" onClick={() => setOpen(false)}
                className="inline-block mt-4 text-amber font-medium underline">Browse products</Link>
            </div>
          ) : (
            <div className="space-y-5">
              {cart.lines.map((l, i) => (
                <div key={i} data-testid={`cart-line-${l.productId}`} className="flex gap-3">
                  <img src={l.image} alt={l.title} className="w-20 h-24 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-espresso/50">{l.brand}</p>
                    <p className="font-medium text-sm leading-snug">{l.title}</p>
                    {l.variantTitle && <p className="text-xs text-espresso/60">{l.variantTitle}</p>}
                    {l.isWhiteGlove && <p className="text-xs text-forest flex items-center gap-1"><Truck size={11} />White Glove Delivery</p>}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-black/15">
                        <button data-testid={`cart-dec-${l.productId}`} className="px-2 py-1 hover:bg-black/5"
                          onClick={() => updateItem({ ...l, quantity: l.quantity - 1 })}><Minus size={13} /></button>
                        <span className="px-3 text-sm">{l.quantity}</span>
                        <button data-testid={`cart-inc-${l.productId}`} className="px-2 py-1 hover:bg-black/5"
                          onClick={() => updateItem({ ...l, quantity: l.quantity + 1 })}><Plus size={13} /></button>
                      </div>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${l.productId}`} className="text-espresso/40 hover:text-destructive self-start"
                    onClick={() => removeItem(l)}><Trash2 size={16} /></button>
                </div>
              ))}

              {wgReminder.length > 0 && (
                <div data-testid="cart-wg-reminder" className="bg-forest/10 border border-forest/30 p-4 text-sm">
                  <p className="font-medium text-forest flex items-center gap-2"><Truck size={15} /> White glove delivery available</p>
                  <p className="text-espresso/70 text-xs mt-1">Need help with {wgReminder.join(", ")}? Let's arrange it.</p>
                  <div className="flex gap-3 mt-2 text-xs">
                    <a href={PHONE_HREF} className="text-amber underline flex items-center gap-1"><Phone size={12} />Call us</a>
                    <a href={GOOGLE_MEET_LINK} target="_blank" rel="noreferrer" className="text-amber underline">Book a call</a>
                  </div>
                </div>
              )}

              {suggestions.length > 0 && (
                <div data-testid="cart-suggestions">
                  <p className="font-serif text-lg mt-4 mb-3">Don't Forget These</p>
                  <div className="space-y-3">
                    {suggestions.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 border border-black/10 p-2">
                        <img src={s.images[0]} alt={s.title} className="w-12 h-12 object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{s.title}</p>
                        </div>
                        <button data-testid={`cart-suggest-add-${s.id}`}
                          onClick={() => addItem({ productId: s.id, quantity: 1 }, { openDrawer: false })}
                          className="text-xs bg-espresso text-cream px-3 py-1.5 hover:bg-charcoal">Add</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {cart.lines.length > 0 && (
          <div className="border-t border-black/10 px-6 py-5">
            <button data-testid="request-quote-btn" onClick={requestQuote}
              className="w-full bg-gold text-espresso py-3.5 font-medium hover:bg-amber transition-colors">
              Request a Quote
            </button>
            <p className="text-center text-xs text-espresso/50 mt-2">Get tailored pricing for your selected items — no obligation.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
